import { createPortal } from 'react-dom'
import { useEffect, useRef } from 'react'
import { router } from '@inertiajs/react'
import { X } from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { EnumIcon, CARE_ACTION_ICONS, WeatherIcon } from '@/lib/icons'
import type { CalendarTask, CalendarLog } from '@/types/calendar'
import type { WeatherDaily } from '@/types/weather'
import type { Overlays } from './OverlayToggles'

interface DaySidebarProps {
  date: string // "YYYY-MM-DD"
  tasks: CalendarTask[]
  logs: CalendarLog[]
  weather: WeatherDaily | undefined
  overlays: Overlays
  onClose: () => void
}

function formatPolishDate(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00')
  return date.toLocaleDateString('pl-PL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

const WMO_PL: Record<number, string> = {
  0: 'Bezchmurnie', 1: 'Prawie bezchmurnie', 2: 'Częściowe zachmurzenie', 3: 'Zachmurzenie',
  45: 'Mgła', 48: 'Mgła szadzista', 51: 'Lekka mżawka', 53: 'Mżawka', 55: 'Gęsta mżawka',
  61: 'Lekki deszcz', 63: 'Deszcz', 65: 'Silny deszcz',
  71: 'Lekki śnieg', 73: 'Śnieg', 75: 'Silny śnieg',
  80: 'Przelotne opady', 81: 'Przelotne opady', 82: 'Silne przelotne opady',
  95: 'Burza', 96: 'Burza z gradem',
}

export function DaySidebar({ date, tasks, logs, weather, overlays, onClose }: DaySidebarProps) {
  const sidebarRef = useRef<HTMLDivElement>(null)

  // Close on click outside sidebar (but not on Andrzej chat)
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (sidebarRef.current && !sidebarRef.current.contains(target)) {
        const andrzejRoot = document.getElementById('andrzej-root')
        if (andrzejRoot?.contains(target)) return
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [onClose])

  const handleComplete = (taskId: number) => {
    sessionStorage.setItem('calendar_selected_date', date)
    router.post(`/care_tasks/${taskId}/complete`)
  }

  const overdueTasks = tasks.filter(t => t.overdue)
  const normalTasks = tasks.filter(t => !t.overdue)

  const content = (
    <div
      ref={sidebarRef}
      className="fixed inset-y-0 left-0 z-40 h-full w-[22rem] max-w-[85vw] bg-paper border-r border-frame/30 shadow-page overflow-y-auto"
      style={{ animation: 'sidebar-left-in 0.25s ease-out' }}
    >
      {/* Header */}
      <div className="flex items-start justify-between p-md border-b border-frame/20">
        <div>
          <h2 className="font-display text-base tracking-wide capitalize">{formatPolishDate(date)}</h2>
        </div>
        <button onClick={onClose} className="text-frame hover:text-ink transition-colors p-xs -m-xs">
          <X weight="bold" size={18} />
        </button>
      </div>

      {/* Weather */}
      {overlays.weather && weather && (
        <div className="p-md border-b border-frame/20">
          <h3 className="font-label text-xs text-frame uppercase tracking-wider mb-xs">Pogoda</h3>
          <div className="flex items-center gap-sm">
            <WeatherIcon code={weather.weather_code} size={28} />
            <div>
              <div className="font-label text-sm">
                {Math.round(weather.temp_min)}° – {Math.round(weather.temp_max)}°
              </div>
              <div className="font-label text-xs text-frame">
                {WMO_PL[weather.weather_code] || 'Brak danych'}
              </div>
              {weather.precipitation_probability > 0 && (
                <div className="font-label text-xs text-water">
                  Opady: {weather.precipitation_probability}%
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tasks */}
      {overlays.tasks && (overdueTasks.length > 0 || normalTasks.length > 0) && (
        <div className="p-md border-b border-frame/20">
          <h3 className="font-label text-xs text-frame uppercase tracking-wider mb-sm">
            Zadania ({tasks.length})
          </h3>
          <div className="space-y-sm">
            {overdueTasks.map(task => (
              <TaskRow key={task.id} task={task} isOverdue onComplete={handleComplete} />
            ))}
            {normalTasks.map(task => (
              <TaskRow key={task.id} task={task} isOverdue={false} onComplete={handleComplete} />
            ))}
          </div>
        </div>
      )}

      {overlays.tasks && tasks.length === 0 && (
        <div className="p-md border-b border-frame/20">
          <h3 className="font-label text-xs text-frame uppercase tracking-wider mb-xs">Zadania</h3>
          <p className="font-label text-xs text-frame/60 italic">Brak zaplanowanych zadań</p>
        </div>
      )}

      {/* Completed */}
      {overlays.completed && logs.length > 0 && (
        <div className="p-md">
          <h3 className="font-label text-xs text-frame uppercase tracking-wider mb-sm">
            Wykonane ({logs.length})
          </h3>
          <div className="space-y-sm">
            {logs.map(log => (
              <div key={log.id} className="flex items-start gap-xs">
                <EnumIcon entry={CARE_ACTION_ICONS[log.action]} size={16} />
                <div className="min-w-0">
                  <div className="font-label text-sm text-gold">
                    {log.action_pl} — <span className="text-ink">{log.plant_name}</span>
                  </div>
                  {log.notes && (
                    <div className="font-body text-xs text-frame italic mt-[2px]">{log.notes}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {overlays.completed && logs.length === 0 && (
        <div className="p-md">
          <h3 className="font-label text-xs text-frame uppercase tracking-wider mb-xs">Wykonane</h3>
          <p className="font-label text-xs text-frame/60 italic">Brak wpisów</p>
        </div>
      )}
    </div>
  )

  return createPortal(content, document.body)
}

function TaskRow({ task, isOverdue, onComplete }: { task: CalendarTask; isOverdue: boolean; onComplete: (id: number) => void }) {
  return (
    <div className="flex items-start gap-xs">
      <EnumIcon entry={CARE_ACTION_ICONS[task.task_type]} size={16} />
      <div className="flex-1 min-w-0">
        <div className={`font-label text-sm ${isOverdue ? 'text-danger font-bold' : ''}`}>
          {task.task_type_pl} — <span className={isOverdue ? 'text-danger' : 'text-ink'}>{task.plant_name}</span>
          {isOverdue && <span className="text-danger text-xs ml-xs">(zaległe!)</span>}
        </div>
        {task.instructions && (
          <div className="font-body text-xs text-frame italic mt-[2px]">{task.instructions}</div>
        )}
      </div>
      <Button size="sm" onClick={() => onComplete(task.id)}>Wykonaj</Button>
    </div>
  )
}
